// src/components/TrainingLibrary/TrainerLibraryPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "../../i18n";
import "../styles/TrainerLibraryPanel.css";
import { Draggable } from "@fullcalendar/interaction";

// ICONOS
import iconRun from "../../assets/icons/running.png";
import iconBike from "../../assets/icons/cycling.png";
import iconSwim from "../../assets/icons/swim.png";
import iconStrength from "../../assets/icons/strength.png";
import iconWalk from "../../assets/icons/walk.png";
import iconRest from "../../assets/icons/dayoff.png";
import iconOther from "../../assets/icons/other.png";

// Fallback local (nunca dejar la librería vacía)
import { getLocalLibrary, mapSportEs } from "./localLibrary";
import API_BASE_URL from '../../config/api';

// TP-aligned helpers para drag de planes
export function makeDragPayload(item) {
  const type = item?.type || item?.kind || (item?.weeks ? "plan" : "workout");
  return {
    type: type === "plan" ? "plan" : "workout",
    id: String(item?._id || item?.id || ""),
    name: String(item?.name || item?.title || ""),
    sport: item?.sport || null,
  };
}
export function attachDrag(e, item) {
  try {
    const p = makeDragPayload(item);
    e.dataTransfer.setData("application/x-tzone-workout", JSON.stringify(p));
    if (p.type === "plan") {
      e.dataTransfer.setData("application/x-tzone-plan", JSON.stringify(p));
    }
    e.dataTransfer.effectAllowed = "copyMove";
  } catch (err) {
    console.warn("drag payload error", err);
  }
}

const API_BASE = `${API_BASE_URL.replace(/\/+$/, '')}/api`;
const BRAND = "#193255";

/** ✅ axios client con auth + cookies (evita 401 si existe token/cookie) */
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

function extractToken(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // "Bearer xxx"
  if (/^bearer\s+/i.test(raw)) return raw.replace(/^bearer\s+/i, "").trim();

  // JSON {"token":"..."} o {"accessToken":"..."} o {"jwt":"..."}
  if (raw.startsWith("{") && raw.endsWith("}")) {
    try {
      const obj = JSON.parse(raw);
      const t = obj?.token || obj?.accessToken || obj?.jwt;
      if (t && String(t).trim()) return String(t).trim();
    } catch {}
  }

  return raw;
}

function pickTokenFromStorage() {
  const keys = ["token", "authToken", "accessToken", "jwt", "tzoneToken", "auth", "user"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    const t = extractToken(v);
    if (t) return t;
  }
  return null;
}

api.interceptors.request.use((config) => {
  const token = pickTokenFromStorage();
  config.headers = config.headers || {};
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** ✅ evita warning de <img src=""> */
function SafeImg({ src, alt, ...rest }) {
  const s = src == null ? "" : String(src).trim();
  if (!s) return null;
  return <img src={s} alt={alt || ""} {...rest} />;
}

// Solo icono/label por deporte (el color lo fija BRAND)
const SPORTS = [
  { value: "running",  label: "Correr",   icon: iconRun,      color: "#7C3AED" },
  { value: "cycling",  label: "Ciclismo", icon: iconBike,     color: "#16A34A" },
  { value: "swimming", label: "Nadar",    icon: iconSwim,     color: "#0891B2" },
  { value: "rest",     label: "Descanso", icon: iconRest,     color: "#64748B" },
  { value: "strength", label: "Fuerza",   icon: iconStrength, color: "#7C3AED" },
  { value: "walking",  label: "Caminar",  icon: iconWalk,     color: "#F59E0B" },
  { value: "other",    label: "Otro",     icon: iconOther,    color: "#374151" },
];

// Acepta alias ES/EN sin romper los iconos existentes
const sportInfo = (v) => {
  const k = String(v || "").toLowerCase();
  const map = {
    run: "running",
    running: "running",
    correr: "running",

    bike: "cycling",
    cycling: "cycling",
    ciclismo: "cycling",

    swim: "swimming",
    swimming: "swimming",
    nadar: "swimming",
    natacion: "swimming",
    "natación": "swimming",

    strength: "strength",
    fuerza: "strength",
    gym: "strength",

    walk: "walking",
    walking: "walking",
    caminar: "walking",

    rest: "rest",
    descanso: "rest",

    other: "other",
    otro: "other",
  };
  const key = map[k] || k;
  return SPORTS.find((s) => s.value === key) || SPORTS[0];
};

// Normaliza a los tipos que usa el calendario (ES)
const normalizeTypeEs = (v) => {
  const k = String(v || "").toLowerCase();
  if (k.startsWith("cor") || k === "running" || k === "run" || k === "correr") return "correr";
  if (k.startsWith("cicl") || k === "cycling" || k === "bike" || k === "ciclismo") return "ciclismo";
  if (
    k.startsWith("nad") ||
    k === "swimming" ||
    k === "swim" ||
    k === "nadar" ||
    k === "natacion" ||
    k === "natación"
  )
    return "nadar";
  if (k.startsWith("fuer") || k === "strength" || k === "gym" || k === "fuerza") return "fuerza";
  if (k.startsWith("cami") || k === "walking" || k === "walk" || k === "caminar") return "caminar";
  if (k.includes("desc") || k === "rest" || k === "descanso") return "día de descanso";
  return "otro";
};

// Etiqueta métrica
const metricLabel = (m, t) =>
  m === "distance"
    ? t("trainerTemplates.metricDistance")
    : m === "elevation"
    ? t("trainerTemplates.metricElevation")
    : m === "tss"
    ? t("trainerTemplates.metricTss")
    : t("trainerTemplates.metricDuration");

// minutos → "HH:mm:ss" (para planned.durationIso)
const toHHMMSS = (min) => {
  const n = Math.max(0, Number(min) || 0);
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

// Distancia normalizada (acepta "10", "10.5", "10,5", "10km", "10k")
const sanitizeDistance = (raw) => {
  const s = String(raw ?? "").toLowerCase().replace(",", ".").replace(/k(m)?\b/g, "").trim();
  const v = parseFloat(s);
  return Number.isFinite(v) && v > 0 ? Number(v) : null;
};

// arma data-event para arrastrar el “plan” como item externo FullCalendar
const buildPlanEventData = (tpl) => {
  const sportKey = tpl?.sport || (Array.isArray(tpl?.sports) ? tpl.sports[0] : "running");
  const typeEs = "plan";
  return {
    title: String(tpl?.name || "Plan"),
    extendedProps: {
      templateId: String(tpl?._id || ""),
      type: typeEs,
      sport: sportKey || null,
      description: String(tpl?.notes || ""),
      plan: {
        id: String(tpl?._id || ""),
        name: String(tpl?.name || ""),
      },
    },
  };
};

export default function TrainerLibraryPanel(props) {
  const { t } = useTranslation();

  // ✅ IMPORTANTE:
  // Antes se mostraba el mensaje "Selecciona un atleta..." y se “tapaba” la librería.
  // Ahora SIEMPRE mostramos las planillas; el atleta solo se marca como contexto (sin mensaje).
  const athleteId = props?.athleteId || null;
  const athleteSelected = Boolean(String(athleteId || "").trim());

  // === datos
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // === UI
  const [openIds, setOpenIds] = useState(() => new Set());
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const detailsCache = useRef(new Map()); // id -> { byDay: Map, daysCount: number }

  // densidad + mini + colapsado (persistentes)
  const [dense, setDense] = useState(() => JSON.parse(localStorage.getItem("tlp-dense") || "true"));
  const [mini, setMini] = useState(() => JSON.parse(localStorage.getItem("tlp-mini") || "true"));
  const [collapsed, setCollapsed] = useState(() => JSON.parse(localStorage.getItem("tlp-collapsed") || "false"));

  useEffect(() => localStorage.setItem("tlp-dense", JSON.stringify(dense)), [dense]);
  useEffect(() => localStorage.setItem("tlp-mini", JSON.stringify(mini)), [mini]);
  useEffect(() => localStorage.setItem("tlp-collapsed", JSON.stringify(collapsed)), [collapsed]);

  // Autocolapsar en pantallas angostas
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 1280) setCollapsed(true);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ====== Fallback local ======
  const localTemplates = useMemo(() => {
    try {
      const lib = getLocalLibrary(); // { base:[], threshold:[], vo2:[] }
      const flat = [...(lib?.base || []), ...(lib?.threshold || []), ...(lib?.vo2 || [])];

      // ✅ si viene vacío, crea 3 plantillas mínimas (para que SIEMPRE veas algo)
      if (!flat.length) {
        flat.push(
          {
            name: t("trainingLibraryPanel.easyRun"),
            sport: "correr",
            expected: { durationMin: 45, tss: 40 },
            intensity: "base",
            description: t("trainingLibraryPanel.z2Easy"),
          },
          {
            name: t("trainingLibraryPanel.threshold"),
            sport: "correr",
            expected: { durationMin: 60, tss: 70 },
            intensity: "threshold",
            description: t("trainingLibraryPanel.thresholdBlocks"),
          },
          {
            name: t("trainingLibraryPanel.strength"),
            sport: "fuerza",
            expected: { durationMin: 45, tss: 50 },
            intensity: "strength",
            description: t("trainingLibraryPanel.fullBody"),
          }
        );
      }

      return flat.map((it, i) => {
        const sport = mapSportEs(it.sport || "correr");
        const dur = Math.max(1, Math.round(it?.expected?.durationMin ?? 60));
        const tss = Math.round(it?.expected?.tss ?? 50);
        return {
          _id: it._id || `local-tpl-${i}`,
          name: it.name || t("trainingLibraryPanel.localTemplate"),
          level: it.intensity || "base",
          distanceCategory: "",
          sport,
          sports: [sport],
          defaultDays: 1,
          notes: it.description || "",
          days: [
            {
              day: 1,
              activities: [
                {
                  title: it.name,
                  sport,
                  metric: "duration",
                  value: String(dur),
                  tss,
                  description: it.description || "",
                },
              ],
            },
          ],
          __origin: "local",
        };
      });
    } catch {
      // ✅ incluso si algo explota, devuelve fallback mínimo
      const fallback = [
        {
          _id: "local-fb-1",
          name: t("trainingLibraryPanel.easyRun"),
          level: "base",
          sport: "running",
          sports: ["running"],
          defaultDays: 1,
          notes: t("trainingLibraryPanel.z2Easy"),
          days: [
            {
              day: 1,
              activities: [{ title: t("trainingLibraryPanel.easyRun"), sport: "running", metric: "duration", value: "45", description: "Z2" }],
            },
          ],
          __origin: "local",
        },
      ];
      return fallback;
    }
  }, [t]);

  // ===== inicial =====
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/cycles/templates`);
        const fromApi = Array.isArray(data) ? data : [];
        setTemplates(fromApi);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          console.warn("[LibraryPanel] 401 Unauthorized (token enviado pero rechazado). Usando librería local.");
        } else {
          console.error("[LibraryPanel] Error cargando plantillas:", err);
        }
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [localTemplates]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t2) => `${t2?.name || ""} ${t2?.distanceCategory || ""} ${t2?.level || ""}`.toLowerCase().includes(term));
  }, [q, templates]);

  // ===== helpers detalle (normaliza days/activities) =====
  function normalizeActivitiesFromDays(daysArr = [], fallbackSport = "running") {
    const byDay = new Map();
    let maxDay = 0;

    for (const d of daysArr) {
      const dayNum = Math.max(1, Number(d?.day ?? 1));
      maxDay = Math.max(maxDay, dayNum);
      const acts = Array.isArray(d?.activities) ? d.activities : [];
      for (const a of acts) {
        const norm = {
          day: dayNum,
          sport: a?.sport || fallbackSport || "running",
          title: (a?.title || "").trim() || t("trainerTemplates.untitled"),
          metric: a?.plannedType || a?.metric || "duration",
          value: String(a?.plannedValue ?? a?.value ?? ""),
          description: (a?.description || "").trim(),
        };
        if (!byDay.has(dayNum)) byDay.set(dayNum, []);
        byDay.get(dayNum).push(norm);
      }
    }
    return { byDay, daysCount: maxDay };
  }

  async function fetchDetail(tpl) {
    if (detailsCache.current.has(tpl._id)) return detailsCache.current.get(tpl._id);
    setLoadingDetailId(tpl._id);
    try {
      const { data } = await api.get(`/cycles/templates/${tpl._id}`);
      if (Array.isArray(data?.days) && data.days.length) {
        const result = normalizeActivitiesFromDays(data.days, data?.sport);
        detailsCache.current.set(tpl._id, result);
        return result;
      }
      try {
        const alt = await api.get(`/cycles/templates/${tpl._id}/days`);
        if (Array.isArray(alt?.data) && alt.data.length) {
          const result = normalizeActivitiesFromDays(alt.data, data?.sport || tpl?.sport);
          detailsCache.current.set(tpl._id, result);
          return result;
        }
      } catch {}
      try {
        const altActs = await api.get(`/cycles/templates/${tpl._id}/activities`);
        if (Array.isArray(altActs?.data) && altActs.data.length) {
          const byDay = new Map();
          let maxDay = 0;
          altActs.data.forEach((a) => {
            const dayNum = Math.max(1, Number(a?.day ?? 1));
            maxDay = Math.max(maxDay, dayNum);
            const norm = {
              day: dayNum,
              sport: a?.sport || tpl?.sport || "running",
              title: (a?.title || "").trim() || t("trainerTemplates.untitled"),
              metric: a?.plannedType || a?.metric || "duration",
              value: String(a?.plannedValue ?? a?.value ?? ""),
              description: (a?.description || "").trim(),
            };
            if (!byDay.has(dayNum)) byDay.set(dayNum, []);
            byDay.get(dayNum).push(norm);
          });
          const result = { byDay, daysCount: maxDay };
          detailsCache.current.set(tpl._id, result);
          return result;
        }
      } catch {}
      const empty = { byDay: new Map(), daysCount: 0 };
      detailsCache.current.set(tpl._id, empty);
      return empty;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        console.warn("[LibraryPanel] 401 Unauthorized (detalle). Dejando detalle vacío.");
      } else {
        console.error("[LibraryPanel] Error detalle:", err);
      }
      const empty = { byDay: new Map(), daysCount: 0 };
      detailsCache.current.set(tpl._id, empty);
      return empty;
    } finally {
      setLoadingDetailId(null);
    }
  }

  function toggleOpen(tpl) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(tpl._id)) next.delete(tpl._id);
      else {
        next.add(tpl._id);
        fetchDetail(tpl);
      }
      return next;
    });
  }

  // === Anchura del panel
  const panelWidth = collapsed ? 40 : mini ? 230 : dense ? 300 : 340;

  // activar Draggable sobre TODA la lista
  const extRef = useRef(null);

  useEffect(() => {
    if (!extRef.current) return;
    const draggable = new Draggable(extRef.current, {
      itemSelector: ".fc-external-item",
      eventData: (el) => {
        try {
          return JSON.parse(el.getAttribute("data-event"));
        } catch {
          return { title: el?.innerText?.trim() || t("activityLabels.training") };
        }
      },
    });
    return () => draggable.destroy();
  }, [t]);

  // helper para ordenar días
  const sortedDays = (byDayMap) => [...(byDayMap?.keys?.() ? byDayMap.keys() : [])].sort((a, b) => Number(a) - Number(b));

  return (
    <div
      className={`tlp ${dense ? "tlp--dense" : ""} ${mini ? "tlp--mini" : ""} ${collapsed ? "tlp--collapsed" : ""}`}
      style={{ width: panelWidth, flex: "0 0 auto", position: "relative" }}
      // ✅ usa athleteId sin mostrar mensajes (evita lint y deja contexto disponible)
      data-athlete-selected={athleteSelected ? "1" : "0"}
      data-athlete-id={athleteSelected ? String(athleteId) : ""}
    >
      {/* Barra superior */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="m-0">
          {!collapsed ? (
            <>
              {t("trainerTemplates.library")} <small className="text-muted">v2.6</small>
            </>
          ) : null}
        </h6>
        <div className="d-flex align-items-center gap-2">
          {!collapsed && (
            <>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="tlpMini"
                  checked={mini}
                  onChange={(e) => setMini(e.target.checked)}
                />
                <label htmlFor="tlpMini" className="form-check-label small">
                  {t("trainingLibraryPanel.mini")}
                </label>
              </div>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="tlpDense"
                  checked={dense}
                  onChange={(e) => setDense(e.target.checked)}
                />
                <label htmlFor="tlpDense" className="form-check-label small">
                  {t("trainingLibraryPanel.compact")}
                </label>
              </div>
            </>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? t("trainingLibraryPanel.showLibrary") : t("trainingLibraryPanel.hideLibrary")}
          >
            {collapsed ? "⟵" : "⟶"}
          </button>
        </div>
      </div>

      {/* Colapsado: solo pestaña vertical */}
      {collapsed ? (
        <div
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            position: "absolute",
            right: 8,
            top: 60,
            fontWeight: 600,
            color: "#64748b",
          }}
        >
          {t("trainingLibraryPanel.libraryShort")}
        </div>
      ) : (
        <>
          <input
            className="form-control mb-2"
            placeholder={t("trainerTemplates.searchTemplate")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ height: mini ? 32 : undefined, fontSize: mini ? 13 : undefined }}
          />

          {loading && <div className="text-muted small">{t("common.loading")}</div>}
          {!loading && filtered.length === 0 && <div className="text-muted small">{t("trainerTemplates.noResults")}</div>}

          {/* ✅ IMPORTANTE: aquí ya NO se muestra el bloque/mensaje "Selecciona un atleta..." */}
          <div className="list-group" ref={extRef}>
            {filtered.map((tpl) => {
              const daysArr = Array.isArray(tpl?.days) ? tpl.days : [];
              const maxDayFromPlan = daysArr.reduce((m, d) => Math.max(m, Math.max(1, Number(d?.day ?? 0))), 0);
              const totalDays = Math.max(Number(tpl?.defaultDays || 0), maxDayFromPlan);
              const opened = openIds.has(tpl._id);
              const cached = detailsCache.current.get(tpl._id);

              const sportKey = tpl?.sport || (Array.isArray(tpl?.sports) ? tpl.sports[0] : "running");
              const s = sportInfo(sportKey);

              const planEventData = buildPlanEventData(tpl);

              // === MINI ROW
              if (mini) {
                return (
                  <div
                    key={tpl._id}
                    className="list-group-item p-0 tlp__row"
                    style={{
                      marginBottom: 8,
                      borderRadius: 10,
                      overflow: "hidden",
                      boxShadow: "0 1px 4px rgba(0,0,0,.06)",
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between fc-external-item"
                      style={{
                        background: BRAND,
                        color: "#fff",
                        padding: dense ? "6px 8px" : "8px 10px",
                        cursor: "grab",
                      }}
                      data-event={JSON.stringify(planEventData)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.clearData();
                        attachDrag(e, { ...tpl, type: "plan" });

                        const p = makeDragPayload({ ...tpl, type: "plan" });
                        e.dataTransfer.setData("application/json", JSON.stringify(p));
                        e.dataTransfer.setData("text/plain", JSON.stringify(p));
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                    >
                      <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, flex:1 }}>
                        <div style={{ width:36, height:36, borderRadius:"50%", background:sportInfo(tpl?.sport)?.color||BRAND, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <SafeImg src={s.icon} alt={s.label} width={20} height={20} style={{ filter:"brightness(0) invert(1)" }} />
                        </div>
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:"#fff" }}>{tpl?.name || t("trainingLibraryPanel.untitled")}</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)", marginTop:2 }}>{totalDays || 0} {t("trainingLibraryPanel.daysShort")}</div>
                        </div>
                      </div>
                      <button className="btn btn-xs btn-light" style={{ lineHeight:1, padding:"2px 6px", fontSize:11, flexShrink:0 }} onClick={() => toggleOpen(tpl)} title={opened ? t("trainingLibraryPanel.hideDetail") : t("trainingLibraryPanel.viewDetail")}>
                        {opened ? "−" : "+"}
                      </button>
                    </div>

                    {opened && (
                      <div className="border-top" style={{ padding: dense ? "6px 8px" : "8px 10px", background: "#fff" }}>
                        {!cached && loadingDetailId === tpl._id && <div className="text-muted small">{t("trainingLibraryPanel.loadingDetail")}</div>}

                        {cached && (
                          <div>
                            {sortedDays(cached.byDay).map((day) => (
                              <div key={day} className="mb-1">
                                <div className="fw-semibold small mb-1" style={{ fontSize: 12 }}>
                                  {t("trainerTemplates.day")} {day}
                                </div>

                                {(cached.byDay.get(day) || []).map((act, idx) => {
                                  const si = sportInfo(act.sport);
                                  const typeEs = normalizeTypeEs(act.sport || tpl.sport || "");
                                  const isDist = act.metric === "distance" && sanitizeDistance(act.value) != null;
                                  const isDur = act.metric === "duration" && Number(act.value) > 0;

                                  return (
                                    <div
                                      key={idx}
                                      className="d-flex align-items-center justify-content-between border rounded mb-1 fc-external-item"
                                      style={{ padding: "4px 6px", gap: 6, cursor: "grab" }}
                                      data-type={typeEs}
                                      data-metric={act.metric || ""}
                                      data-value={String(act.value ?? "")}
                                      data-distance={isDist ? String(act.value ?? "") : undefined}
                                      data-duration={isDur ? toHHMMSS(act.value ?? 0) : undefined}
                                      data-event={JSON.stringify({
                                        title: String(act.title || tpl.name || t("activityLabels.training")),
                                        extendedProps: {
                                          templateId: String(tpl._id || ""),
                                          type: typeEs,
                                          sport: act.sport || tpl.sport || null,
                                          description: act.description || "",
                                          planned: isDist
                                            ? { distancia: sanitizeDistance(act.value) }
                                            : isDur
                                            ? { durationIso: toHHMMSS(act.value) }
                                            : {},
                                        },
                                      })}
                                    >
                                      <div className="d-flex align-items-center" style={{ gap: 6, minWidth: 0 }}>
                                        <SafeImg src={si.icon} alt={si.label} width={14} height={14} />
                                        <div
                                          className="small"
                                          style={{
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            minWidth: 0,
                                          }}
                                        >
                                          <span className="fw-semibold" style={{ fontSize: 12 }}>
                                            {act.title}
                                          </span>{" "}
                                          <span className="text-muted" style={{ fontSize: 11 }}>
                                            · {metricLabel(act.metric, t)} {act.value}
                                          </span>
                                        </div>
                                      </div>
                                      <span className="badge text-bg-light" style={{ fontSize: 10 }}>
                                        {si.label}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              // === MODO TARJETA
              return (
                <div
                  key={tpl._id}
                  className="list-group-item p-0 tlp__item"
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,.06)",
                    marginBottom: 10,
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center tlp__head fc-external-item"
                    style={{
                      padding: dense ? "6px 10px" : "8px 12px",
                      background: BRAND,
                      color: "white",
                      cursor: "grab",
                    }}
                    data-event={JSON.stringify(planEventData)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.clearData();
                      attachDrag(e, { ...tpl, type: "plan" });

                      const p = makeDragPayload({ ...tpl, type: "plan" });
                      e.dataTransfer.setData("application/json", JSON.stringify(p));
                      e.dataTransfer.setData("text/plain", JSON.stringify(p));
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                  >
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, flex:1 }}>
                      <div style={{ width:40, height:40, borderRadius:"50%", background:sportInfo(tpl?.sport)?.color||BRAND, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <SafeImg src={s.icon} alt={s.label} width={22} height={22} style={{ filter:"brightness(0) invert(1)" }} />
                      </div>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:dense?13:14, color:"#fff" }}>{tpl?.name || t("trainingLibraryPanel.untitled")}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginTop:2 }}>{totalDays||0} {t("trainingLibraryPanel.days")}</div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="d-flex justify-content-between align-items-center p-2"
                    style={{ padding: dense ? "6px 8px" : "8px 12px" }}
                  >
                    <div className="text-muted small" style={{ fontSize: dense ? 12 : 13 }}>
                      {tpl?.notes || " "}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button className={`btn btn-sm btn-outline-secondary ${dense ? "py-0" : ""}`} onClick={() => toggleOpen(tpl)}>
                        {opened ? t("trainingLibraryPanel.hide") : t("trainingLibraryPanel.viewDetail")}
                      </button>
                    </div>
                  </div>

                  {opened && (
                    <div className="border-top" style={{ maxHeight:400, overflowY:"auto" }}>
                      {!cached && loadingDetailId === tpl._id && <div className="p-2 text-muted small">{t("trainingLibraryPanel.loadingDetail")}</div>}

                      {cached && (
                        <div className="p-2" style={{ padding: dense ? "6px 8px" : "8px 12px" }}>
                          {sortedDays(cached.byDay).map((day) => (
                            <div key={day} className="mb-2">
                              <div className="fw-semibold small mb-1" style={{ fontSize: dense ? 12 : 13 }}>
                                {t("trainerTemplates.day")} {day}
                              </div>

                              {(cached.byDay.get(day) || []).map((act, idx) => {
                                const si = sportInfo(act.sport);
                                const typeEs = normalizeTypeEs(act.sport || tpl.sport || "");
                                const isDist = act.metric === "distance" && sanitizeDistance(act.value) != null;
                                const isDur = act.metric === "duration" && Number(act.value) > 0;

                                return (
                                  <div
                                    key={idx}
                                    className="d-flex align-items-center justify-content-between border rounded p-2 mb-1 fc-external-item"
                                    style={{ padding: dense ? "4px 6px" : "8px", gap: dense ? 6 : 8, cursor: "grab" }}
                                    data-type={typeEs}
                                    data-metric={act.metric || ""}
                                    data-value={String(act.value ?? "")}
                                    data-distance={isDist ? String(act.value ?? "") : undefined}
                                    data-duration={isDur ? toHHMMSS(act.value ?? 0) : undefined}
                                    data-event={JSON.stringify({
                                      title: String(act.title || tpl.name || t("activityLabels.training")),
                                      extendedProps: {
                                        templateId: String(tpl._id || ""),
                                        type: typeEs,
                                        sport: act.sport || tpl.sport || null,
                                        description: act.description || "",
                                        planned: isDist
                                          ? { distancia: sanitizeDistance(act.value) }
                                          : isDur
                                          ? { durationIso: toHHMMSS(act.value) }
                                          : {},
                                      },
                                    })}
                                  >
                                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                      <SafeImg src={si.icon} alt={si.label} width={dense ? 14 : 18} height={dense ? 14 : 18} />
                                      <div className="small" style={{ minWidth: 0 }}>
                                        <div
                                          className="fw-semibold"
                                          style={{
                                            fontSize: dense ? 12 : 13,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                          title={act.title}
                                        >
                                          {act.title}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: dense ? 11 : 12 }}>
                                          {metricLabel(act.metric, t)}: {act.value}
                                        </div>
                                      </div>
                                    </div>
                                    <span className="badge text-bg-light" style={{ fontSize: dense ? 11 : 12 }}>
                                      {si.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}